// src/components/macro/fx/FXMoversCard.tsx
/**
 * FX Movers Card Component
 *
 * Displays strongest or weakest currencies.
 * Each card has its own independent time range filter.
 */

import React, { useState, useMemo } from "react";
import { TrendingUp, TrendingDown } from "lucide-react";
import { getCurrencyFlag } from "@/utils/countryFlags";
import type { FxCommodity } from "@/types/dashboards/macro";

type MoversTimeRange = "day" | "month" | "ytd" | "yoy";

interface FXMoverItem {
  asset_name: string;
  price: number | null;
  change_pct: number | null;
  category: string | null;
}

/**
 * Time range picker for movers cards
 */
const MoversTimeRangePicker: React.FC<{
  value: MoversTimeRange;
  onChange: (value: MoversTimeRange) => void;
}> = ({ value, onChange }) => {
  const options: { key: MoversTimeRange; label: string }[] = [
    { key: "day", label: "Day" },
    { key: "month", label: "Month" },
    { key: "ytd", label: "YTD" },
    { key: "yoy", label: "YoY" },
  ];

  return (
    <div className="flex gap-0.5">
      {options.map(({ key, label }) => (
        <button
          key={key}
          onClick={() => onChange(key)}
          className={`px-1.5 py-0.5 text-[10px] font-medium rounded transition-all ${
            value === key
              ? "bg-blue-500/20 text-blue-400"
              : "text-slate-400 hover:text-slate-300"
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );
};

/**
 * Get row styling based on rank (top 3 highlighted)
 * Strongest = emerald/green theme, Weakest = rose/red theme
 */
const getRowBgClass = (
  idx: number,
  direction: "strongest" | "weakest"
): string => {
  if (direction === "strongest") {
    if (idx === 0)
      return "bg-gradient-to-r from-emerald-500/15 via-emerald-500/5 to-transparent border-l-2 border-emerald-500/50";
    if (idx === 1)
      return "bg-gradient-to-r from-slate-400/10 via-slate-400/5 to-transparent border-l-2 border-slate-400/40";
    if (idx === 2)
      return "bg-gradient-to-r from-teal-700/10 via-teal-700/5 to-transparent border-l-2 border-teal-700/40";
  } else {
    if (idx === 0)
      return "bg-gradient-to-r from-rose-500/15 via-rose-500/5 to-transparent border-l-2 border-rose-500/50";
    if (idx === 1)
      return "bg-gradient-to-r from-slate-400/10 via-slate-400/5 to-transparent border-l-2 border-slate-400/40";
    if (idx === 2)
      return "bg-gradient-to-r from-rose-700/10 via-rose-700/5 to-transparent border-l-2 border-rose-700/40";
  }
  return "odd:bg-gray-900/20";
};

/**
 * Get rank number color based on position
 */
const getRankColorClass = (
  idx: number,
  direction: "strongest" | "weakest"
): string => {
  if (direction === "strongest") {
    if (idx === 0) return "text-emerald-400 font-semibold";
    if (idx === 1) return "text-slate-300 font-semibold";
    if (idx === 2) return "text-teal-400 font-semibold";
  } else {
    if (idx === 0) return "text-rose-400 font-semibold";
    if (idx === 1) return "text-slate-300 font-semibold";
    if (idx === 2) return "text-rose-400/70 font-semibold";
  }
  return "text-slate-500";
};

interface FXMoversCardProps {
  title: string;
  data: FxCommodity[];
  isLoading: boolean;
  direction: "strongest" | "weakest";
  /** Number of items to display (default: 5) */
  limit?: number;
  /** Whether to fill the container height (for widget use) */
  fillHeight?: boolean;
}

/**
 * FX Movers Card - Shows strongest or weakest currencies
 * Note: For USD pairs, when pair goes DOWN = currency strengthened vs USD
 *       When pair goes UP = currency weakened vs USD
 */
const FXMoversCard: React.FC<FXMoversCardProps> = ({
  title,
  data,
  isLoading,
  direction,
  limit = 5,
  fillHeight = false,
}) => {
  // Each card has its own time range state
  const [timeRange, setTimeRange] = useState<MoversTimeRange>("day");

  // Strongest currencies have negative change (pair went down = currency appreciated)
  // Weakest currencies have positive change (pair went up = currency depreciated)
  const valueColor =
    direction === "strongest" ? "text-emerald-400" : "text-rose-400";

  // Card styling based on direction
  const cardBorderClass =
    direction === "strongest" ? "border-emerald-500/20" : "border-rose-500/20";

  const iconBgClass =
    direction === "strongest"
      ? "bg-gradient-to-br from-emerald-500/20 to-teal-500/20 text-emerald-400"
      : "bg-gradient-to-br from-rose-500/20 to-red-500/20 text-rose-400";

  // Calculate movers based on this card's time range
  const movers = useMemo(() => {
    if (!data.length) return [];

    const getChangePct = (item: FxCommodity): number | null => {
      switch (timeRange) {
        case "day":
          return item.day_change_pct;
        case "month":
          return item.monthly_change_pct;
        case "ytd":
          return item.ytd_change_pct;
        case "yoy":
          return item.yoy_change_pct;
        default:
          return item.day_change_pct;
      }
    };

    const moverItems: FXMoverItem[] = data
      .map((item) => ({
        asset_name: item.asset_name,
        price: item.price,
        change_pct: getChangePct(item),
        category: item.category,
      }))
      .filter(
        (item) => item.change_pct != null && Number.isFinite(item.change_pct)
      );

    // Strongest currencies: pair went DOWN the most (negative change = currency appreciated vs USD)
    // Weakest currencies: pair went UP the most (positive change = currency depreciated vs USD)
    if (direction === "strongest") {
      return [...moverItems]
        .sort((a, b) => (a.change_pct ?? 0) - (b.change_pct ?? 0))
        .slice(0, limit);
    } else {
      return [...moverItems]
        .sort((a, b) => (b.change_pct ?? 0) - (a.change_pct ?? 0))
        .slice(0, limit);
    }
  }, [data, timeRange, direction, limit]);

  const formatPct = (value: number | null): string => {
    if (value == null || !Number.isFinite(value)) return "–";
    const sign = value >= 0 ? "+" : "";
    return `${sign}${value.toFixed(2)}%`;
  };

  const formatPrice = (value: number | null): string => {
    if (value == null || !Number.isFinite(value)) return "–";
    return value.toFixed(4);
  };

  return (
    <div
      className={`rounded-xl bg-gradient-to-br from-gray-800/40 via-gray-800/30 to-gray-800/40 border ${cardBorderClass} p-2.5 sm:p-3 ${
        fillHeight ? "h-full flex flex-col" : ""
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-2 pb-2 border-b border-slate-700/40 flex-shrink-0">
        <div className="flex items-center gap-2">
          <div
            className={`w-5 h-5 rounded-md ${iconBgClass} flex items-center justify-center`}
          >
            {direction === "strongest" ? (
              <TrendingUp className="w-3 h-3" />
            ) : (
              <TrendingDown className="w-3 h-3" />
            )}
          </div>
          <div className="text-xs uppercase tracking-wide text-slate-300 font-medium">
            {title}
          </div>
        </div>
        <MoversTimeRangePicker value={timeRange} onChange={setTimeRange} />
      </div>

      {/* Column Headers */}
      <div className="grid grid-cols-[8%_37%_27%_28%] gap-1 items-center px-1.5 pb-1.5 text-[10px] font-medium text-slate-400 border-b border-gray-700/30 flex-shrink-0">
        <div>#</div>
        <div>Pair</div>
        <div className="text-right">Price</div>
        <div className="text-right">Change</div>
      </div>

      {/* Movers List */}
      <ul className={fillHeight ? "flex-1 min-h-0 overflow-y-auto" : ""}>
        {isLoading ? (
          <li className="py-3 text-center text-xs text-slate-400">
            Loading...
          </li>
        ) : movers.length === 0 ? (
          <li className="py-3 text-center text-xs text-slate-400">
            No data available
          </li>
        ) : (
          movers.map((item, idx) => {
            const flag = getCurrencyFlag(item.asset_name);
            const rowBgClass = getRowBgClass(idx, direction);
            const rankColorClass = getRankColorClass(idx, direction);

            return (
              <li
                key={item.asset_name}
                className={`${rowBgClass} transition-all duration-200 hover:scale-[1.01]`}
              >
                <div className="grid grid-cols-[8%_37%_27%_28%] gap-1 items-center py-1.5 px-1.5">
                  {/* Rank */}
                  <div className={`tabular-nums text-xs ${rankColorClass}`}>
                    {idx + 1}
                  </div>

                  {/* Pair info */}
                  <div className="flex items-center gap-1.5 min-w-0">
                    {flag && (
                      <span className="text-sm flex-shrink-0">{flag}</span>
                    )}
                    <span
                      className={`text-xs font-medium truncate ${
                        idx < 3 ? "text-white" : "text-slate-200"
                      }`}
                    >
                      {item.asset_name}
                    </span>
                  </div>

                  {/* Price */}
                  <div className="text-right tabular-nums text-xs text-slate-300">
                    {formatPrice(item.price)}
                  </div>

                  {/* Move */}
                  <div
                    className={`text-right tabular-nums text-xs font-semibold ${valueColor}`}
                  >
                    {formatPct(item.change_pct)}
                  </div>
                </div>
              </li>
            );
          })
        )}
      </ul>
    </div>
  );
};

export default FXMoversCard;
